import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Sidebar from './components/Sidebar';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import AddTransactionPage from './pages/AddTransactionPage';
import TransactionListPage from './pages/TransactionListPage';
import MachineTypesPage from './pages/MachineTypesPage';
import CooperativeMachinePerformanceReportSecretary from './pages/CooperativeMachinePerformanceReportSecretary';
import CooperativeMachinePerformanceReportInspector from './pages/CooperativeMachinePerformanceReportInspector';
import InspectorSocietyListPage from './pages/InspectorSocietyListPage';
import InspectorTransactionListPage from './pages/InspectorTransactionListPage';
import AuthorizedMachineCountPage from './pages/AuthorizedMachineCountPage';
import CooperativeMachinePerformanceReportAR from './pages/CooperativeMachinePerformanceReportAR';
import CooperativeMachinePerformanceReportDR from './pages/CooperativeMachinePerformanceReportDR';
import UserListPage from './pages/UserListPage';
import ZeroTransactionsReportPage from './pages/ZeroTransactionsReportPage';

function DashboardLayout({ user, onLogout, onSwitchSociety }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const userRole = (user.role || '').toLowerCase();
  const isSecretary = userRole === 'secretary';
  const isAR = userRole === 'ar';
  const isDR = userRole === 'dr';
  const isRCS = userRole === 'rcs';
  const isPower = userRole === 'power';

  const secretaryMenu = [
    { name: 'Home', path: '/home', icon: '🏠' },
    { name: 'Add Transaction', path: '/add-transaction', icon: '➕' },
    { name: 'Transaction List', path: '/transactions', icon: '📋' },
    { name: 'View / Edit Machine Types', path: '/machine-types', icon: '📝' },
    { name: 'Cooperative Machine Performance Report', path: '/reports', icon: '📊' }
  ];

  const arMenu = [
    { name: 'Home', path: '/home', icon: '🏠' },
    { name: 'Cooperative Machine Performance Report', path: '/reports', icon: '📊' }
  ];

  const drMenu = [
    { name: 'Cooperative Machine Performance Report', path: '/reports', icon: '📊' }
  ];

  const rcsMenu = [
    { name: 'Home', path: '/home', icon: '🏠' },
    { name: 'Society List', path: '/societies', icon: '🏛️' },
    { name: 'Transaction List', path: '/transactions', icon: '📋' },
    { name: 'Cooperative Machine Performance Report', path: '/reports', icon: '📊' },
    { name: 'Societies with Zero Transactions', path: '/zero-transactions-societies', icon: '🚫' }
  ];

  const powerMenu = [
    { name: 'Home', path: '/home', icon: '🏠' },
    { name: 'Society List', path: '/societies', icon: '🏛️' },
    { name: 'User List', path: '/users', icon: '👥' },
    { name: 'Transaction List', path: '/transactions', icon: '📋' },
    { name: 'Cooperative Machine Performance Report', path: '/reports', icon: '📊' },
    { name: 'Societies with Zero Transactions', path: '/zero-transactions-societies', icon: '🚫' }
  ];

  const inspectorMenu = [
    { name: 'Home', path: '/home', icon: '🏠' },
    { name: 'Society List', path: '/societies', icon: '🏛️' },
    { name: 'Transaction List', path: '/transactions', icon: '📋' },
    { name: 'Cooperative Machine Performance Report', path: '/reports', icon: '📊' }
  ];

  const menuItems = isPower ? powerMenu : (isSecretary ? secretaryMenu : (isAR ? arMenu : (isDR ? drMenu : (isRCS ? rcsMenu : inspectorMenu))));

  const currentMenuItem = menuItems.find(m => m.path === location.pathname);
  const pageTitle = currentMenuItem ? currentMenuItem.name : 'Dashboard';

  return (
    <div className="dashboard-container">
      <Sidebar
        user={user}
        menuItems={menuItems}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        onLogout={onLogout}
        onSwitchSociety={onSwitchSociety}
      />

      <main className="main-content">
        {/* Fixed Mobile Top Header */}
        <header className="mobile-header">
          <button
            className="hamburger-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open Navigation Menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1b2559" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <h1 className="mobile-header-title">Cooperative Machinery Tracker v2.0</h1>
          <div style={{ width: '36px' }}></div> {/* Spacer to keep title centered */}
        </header>

        <div className="page-scroll-area">
          <Routes>
            <Route path="/home" element={isDR ? <Navigate to="/reports" replace /> : <HomePage user={user} isSecretary={isSecretary} />} />
            <Route path="/add-transaction" element={<AddTransactionPage user={user} />} />
            <Route
              path="/transactions"
              element={
                isSecretary ? (
                  <TransactionListPage user={user} />
                ) : (
                  <InspectorTransactionListPage user={user} />
                )
              }
            />
            <Route path="/societies" element={<InspectorSocietyListPage user={user} />} />
            <Route path="/users" element={<UserListPage user={user} />} />
            <Route path="/authorized-machine-counts" element={<AuthorizedMachineCountPage user={user} />} />
            <Route path="/machine-types" element={<MachineTypesPage user={user} />} />
            <Route path="/zero-transactions-societies" element={<ZeroTransactionsReportPage user={user} />} />
            <Route
              path="/reports"
              element={
                isSecretary ? (
                  <CooperativeMachinePerformanceReportSecretary user={user} />
                ) : isAR ? (
                  <CooperativeMachinePerformanceReportAR user={user} />
                ) : (isDR || isRCS || isPower) ? (
                  <CooperativeMachinePerformanceReportDR user={user} mode={(isRCS || isPower) ? 'rcs' : 'dr'} />
                ) : (
                  <CooperativeMachinePerformanceReportInspector user={user} />
                )
              }
            />
            <Route path="*" element={<Navigate to={isDR ? "/reports" : "/home"} replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('ips_user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const handleLogout = () => {
    localStorage.removeItem('ips_auth_token');
    localStorage.removeItem('ips_user');
    setUser(null);
  };

  const handleSwitchSociety = (newSociety) => {
    if (!user) return;
    const updatedUser = {
      ...user,
      society: newSociety
    };
    setUser(updatedUser);
    localStorage.setItem('ips_user', JSON.stringify(updatedUser));
  };

  const role = (user?.role || '').toLowerCase();
  const defaultPath = role === 'dr' ? '/reports' : '/home';

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            user ? <Navigate to={defaultPath} replace /> : <LoginPage onLoginSuccess={(userData) => setUser(userData)} />
          }
        />
        <Route
          path="/*"
          element={
            user ? <DashboardLayout user={user} onLogout={handleLogout} onSwitchSociety={handleSwitchSociety} /> : <Navigate to="/login" replace />
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
